import { Injectable } from '@angular/core';
import { io, Socket } from 'socket.io-client';
import { Subject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TtsStreamService {

  private socket!: Socket;
  private isConnected = false;

  // Stream events
  private streamStartedSubject = new Subject<any>();
  streamStarted$ = this.streamStartedSubject.asObservable();

  private audioChunkSubject = new Subject<any>();
  audioChunk$ = this.audioChunkSubject.asObservable();

  private streamCompleteSubject = new Subject<any>();
  streamComplete$ = this.streamCompleteSubject.asObservable();

  private errorSubject = new Subject<any>();
  error$ = this.errorSubject.asObservable();

  private wordTimestampSubject = new Subject<any>();
  wordTimestamp$ = this.wordTimestampSubject.asObservable();

  private connectionStatusSubject = new Subject<boolean>();
  connectionStatus$ = this.connectionStatusSubject.asObservable();

  private playbackEndedSubject = new Subject<void>();
  playbackEnded$ = this.playbackEndedSubject.asObservable();

  private connectedSubject = new Subject<void>();
  connected$ = this.connectedSubject.asObservable();

  private aiChunks: Blob[] = [];


  // ================================
  // CONNECT (with handshake promise)
  // ================================
  connect(url: string): Promise<void> {

    return new Promise((resolve, reject) => {

      if (this.socket && this.isConnected) {
        resolve();
        return;
      }

      if (this.socket) {
        this.socket.removeAllListeners();
        this.socket.disconnect();
      }

      this.socket = io(url, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5
      });

      this.socket.on('connect', () => {
        console.log('Socket connected:', this.socket.id);
        this.isConnected = true;
        this.connectionStatusSubject.next(true);
        this.connectedSubject.next();
        resolve(); // ✅ handshake completed
      });

      this.socket.on('disconnect', () => {
        console.log('Socket disconnected');
        this.isConnected = false;
        this.connectionStatusSubject.next(false);
      });

      this.socket.on('connect_error', (err) => {
        console.error('Socket connect error:', err);
        this.errorSubject.next(err);
        reject(err);
      });


      // Stream events
      this.socket.on('stream_started', (data) => {
        this.streamStartedSubject.next(data);
      });

      this.socket.on('audio_chunk', (data) => {
        this.audioChunkSubject.next(data);
      });

      this.socket.on('stream_complete', (data) => {
        this.streamCompleteSubject.next(data);
      });

      this.socket.on('word_timestamp', (data) => {
        this.wordTimestampSubject.next(data);
      });

      this.socket.on('error', (data) => {
        this.errorSubject.next(data);
      });

    });
  }



  // ================================
  // START STREAM
  // ================================
  startStreaming(payload: any): void {

    if (!this.socket || !this.isConnected) {
      this.errorSubject.next('Socket not connected');
      return;
    }

    this.socket.emit('start_streaming', payload);
  }


  // ================================
  // STOP STREAM
  // ================================
  stopStreaming(): void {
    if (this.socket && this.isConnected) {
      this.socket.emit('stop_streaming');
    }
  }


  // ================================
  // PLAYBACK COMPLETE (trigger manually)
  // ================================
  notifyPlaybackEnded(): void {
    this.playbackEndedSubject.next();
  }

  // ================================
  // SEND TEXT CHUNK (Real-time)
  // ================================
  sendChunk(textChunk: string): void {

    if (!this.socket || !this.isConnected) {
      this.errorSubject.next('Socket not connected');
      return;
    }

    this.socket.emit('send_chunk', {
      text: textChunk
    });
  }

  // ================================
  // END STREAM (Real-time)
  // ================================
  endStream(): void {

    if (!this.socket || !this.isConnected) {
      this.errorSubject.next('Socket not connected');
      return;
    }

    this.socket.emit('end_stream');
  }



  // ================================
  // DISCONNECT
  // ================================
  disconnect(): void {

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.isConnected = false;
      this.connectionStatusSubject.next(false);
    }
  }
}
